/**
 * Every word the public signup page (/signup) says, in one place (design record §7b rule 1: a page's words live once,
 * and both its looks read them). Today's page (src/pages/Signup.tsx) and the new design's rebuild read this module, so
 * the two can never say different things; a parity test compares them word for word.
 *
 * The consent lines are the SMS program's disclosure (PRE-72: Twilio A2P 10DLC and toll-free verification reviewers
 * read this page). Change them only with the program's owner, and keep public/signup.html, the static copy a direct
 * visit to prest0.ai/signup is served, saying the same thing.
 *
 * The error lines are D9 failure copy: each names the next step.
 */
export const SIGNUP_WORDS = {
  // What the tab and search engines are told, one variant per screen.
  seo: {
    form: {
      title: 'Create your Prest0n account — prest0',
      description: 'Create a Prest0n account and opt in to SMS notifications for account and client-communication events.',
    },
    created: {
      title: 'Account created — prest0',
      description: 'Your Prest0n account has been created. Check your email for a verification link.',
    },
    checkPhone: {
      title: 'Check your phone — prest0',
      description: 'Confirm your SMS opt-in by replying YES to the text message we just sent.',
    },
    canonicalUrl: 'https://prest0.ai/signup',
  },
  form: {
    title: 'Create your Prest0n account',
    lead: 'Sign up for free. SMS notifications for your account and client communications are optional.',
    ariaLabel: 'Create account',
    name: 'Full name',
    email: 'Email',
    phone: 'Phone (US)',
    phoneHint: ' — e.g. (628) 444-4018',
    company: 'Company',
    companyHint: '(optional)',
    password: 'Password',
    passwordHint: ' — at least 10 characters, mixed case, with a number',
    // The SMS consent line, in its parts: the optional marker, the disclosure, and its two links.
    sms: {
      optional: '(Optional)',
      body: ' I agree to receive SMS from Prest0n about my account and client-communication notifications. Msg & data rates may apply. Reply STOP to opt out, HELP for help. Frequency varies. See ',
      privacy: { label: 'Privacy Policy', href: '/privacy-policy#sms' },
      and: ' and ',
      terms: { label: 'SMS Terms', href: '/sms-terms' },
      end: '.',
    },
    // The terms line, in its parts.
    tos: {
      lead: 'I agree to the ',
      terms: { label: 'Terms of Service', href: '/terms-of-service' },
      and: ' and ',
      privacy: { label: 'Privacy Policy', href: '/privacy-policy' },
      end: '.',
    },
    submit: 'Create account',
    submitting: 'Creating account…',
  },
  footer: {
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    separator: '·',
    needCode: 'Need a trial access code?',
    startTrial: 'Start trial',
  },
  // After an account is made without SMS: it is made, and the email carries the verification link.
  created: {
    title: 'Account created',
    lead: "You're all set. Check your email for the verification link.",
    cardTitle: "You're in",
    body: "You did not opt in to SMS notifications. That's fine — you can opt in later from your account settings if you change your mind.",
    signIn: 'Sign in to your new account',
  },
  // After an account is made WITH SMS: the double opt-in text is on its way, and it can be sent again.
  checkPhone: {
    title: 'Check your phone',
    leadBefore: 'We just sent a confirmation text. Reply ',
    yes: 'YES',
    leadAfter: ' to finish opting in.',
    cardTitle: 'One more step',
    replyBefore: 'Reply ',
    replyMiddle: ' to the text we just sent. You can reply ',
    stop: 'STOP',
    replyStopAfter: ' at any time to opt out, or ',
    help: 'HELP',
    replyEnd: ' for support.',
    continue: 'continue to setup →',
    resend: 'Resend confirmation SMS',
    resending: 'Resending…',
    resent: 'Sent — check your phone again in a moment.',
    resendFailed: 'Could not resend right now. Try again in a minute.',
    noTextLead: "Didn't get the text?",
    signIn: 'Sign in to your new account',
    noTextEnd: ' and you can retry from account settings.',
  },
  errors: {
    phone: 'Please enter a valid US phone number.',
    passwordGeneric: 'Password does not meet requirements.',
    passwordLength: 'Password must be at least 10 characters.',
    passwordLower: 'Password must include a lowercase letter.',
    passwordUpper: 'Password must include an uppercase letter.',
    passwordDigit: 'Password must include a number.',
    emailInUse: 'This email is already registered. Please sign in instead.',
    weakPassword: 'Password is too weak. Use at least 10 characters with mixed case and a number.',
    invalidEmail: 'Please enter a valid email address.',
    generic: 'Something went wrong while creating your account. Please try again.',
  },
} as const;
